import { type RefObject, useEffect, useState } from 'react';

import type { Tour, TourFrame, TourStep } from '@carboncopyco/types';
import { LoadingDots } from '@carboncopyco/ui';

import { useTourSelector } from '../../../../hooks';
import {
  selectDraftSettings,
  selectFocusFrameIndex,
  selectIsGeneratingReplayFrameChunks,
  selectIsScreenshotAsFrameUploading,
} from '../../../../store';
import { useTourEditorPageProps } from '../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../ToursReplayerEditor';
import { Frame, LoadingFrame } from './Frame';
import useFrameWarnings from './Frame/useFrameWarnings';
import { SortableItem } from './SortableItem';
import { SortableList } from './SortableList';
import useFrameListActions from './useFrameListActions';

interface IFrameList {
  tourId: string;
  frames: TourFrame[];
  activeFrameId: TourFrame['id'];
  replayerRef: RefObject<ReplayerEditorRef>;
  doReplay: (step: TourStep) => void;
  fileServiceApiToken: Tour['fileServiceToken'];
}

export const FrameList = ({
  tourId,
  frames,
  activeFrameId,
  replayerRef,
  doReplay,
  fileServiceApiToken,
}: IFrameList) => {
  const { uuid } = useTourEditorPageProps();

  const [localFrames, setLocalFrames] = useState<TourFrame[]>(null);
  const [localFramesChanged, setLocalFramesChanged] = useState(false);
  const frameData = localFrames || frames;
  const draftSettings = useTourSelector(selectDraftSettings(uuid));

  const { saveReorderedFrames } = useFrameListActions({
    tourId,
  });
  const { recalculateWarningsAfterDragging } = useFrameWarnings();

  const isGeneratingReplayFrameChunks = useTourSelector(
    selectIsGeneratingReplayFrameChunks(uuid)
  );
  const isScreenshotAsFrameUploading = useTourSelector(
    selectIsScreenshotAsFrameUploading(uuid)
  );

  const focusFrameIndex = useTourSelector(selectFocusFrameIndex(uuid));

  const handleFramesUpdate = (updatedFrames: TourFrame[]) => {
    setLocalFrames(updatedFrames);
    saveReorderedFrames(updatedFrames);

    const newLastFrame = updatedFrames[updatedFrames.length - 1];
    const oldLastFrame = frameData[frameData.length - 1];

    const isLastFrameUpdated =
      updatedFrames[updatedFrames.length - 1].id !== oldLastFrame.id;

    if (isLastFrameUpdated) {
      recalculateWarningsAfterDragging(
        oldLastFrame,
        newLastFrame,
        draftSettings.showTooltips
      );
    }
    setLocalFramesChanged(true);
  };

  useEffect(() => {
    if (!isGeneratingReplayFrameChunks && localFrames) {
      setLocalFrames(null);
    }
  }, [isGeneratingReplayFrameChunks, localFrames]);

  useEffect(() => {
    if (localFramesChanged && !isGeneratingReplayFrameChunks) {
      setLocalFramesChanged(false);
      replayerRef.current?.refreshTooltip();
    }
  }, [localFramesChanged, isGeneratingReplayFrameChunks]);

  const filteredFrameData = frameData?.filter((frame) => !frame.isDisabled); // we are displaying only non-deleted frames (isDisabled means that frame is deleted)

  return (
    <div
      data-testid="tour-editor-drawer-frame-list"
      className="mt-3 w-full h-full flex flex-col gap-5 overflow-y-auto"
    >
      {isGeneratingReplayFrameChunks && (
        <div className="absolute flex justify-center items-center h-full z-10 left-0 right-0 -mt-24">
          <LoadingDots />
        </div>
      )}
      {filteredFrameData?.length > 0 && replayerRef && (
        <SortableList
          items={frameData}
          displayedItems={filteredFrameData}
          onItemOrderUpdate={handleFramesUpdate}
          className="w-full h-full overflow-x-hidden overflow-y-auto flex flex-col gap-2.5 pr-5 pl-1 pt-1 pb-1"
          renderItem={(frame) => {
            return (
              <>
                <SortableItem id={frame.id}>
                  <Frame
                    frame={frame}
                    frames={frames}
                    key={frame.id}
                    selected={frame.id === activeFrameId}
                    replayerRef={replayerRef}
                    start={frame.frameIndex === 0}
                    isGeneratingReplayFrameChunks={
                      isGeneratingReplayFrameChunks
                    }
                    activeFramesLength={
                      filteredFrameData.filter((frame) => frame.isActive).length
                    }
                    doReplay={doReplay}
                    fileServiceApiToken={fileServiceApiToken}
                  />
                </SortableItem>
                {isScreenshotAsFrameUploading &&
                  frame.frameIndex === focusFrameIndex - 1 && <LoadingFrame />}
              </>
            );
          }}
        />
      )}
    </div>
  );
};
